import React, { useEffect, useRef, useState } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../../../../shared/storage/firebase';
import './dashboardWallpaper.css';

const WALLPAPERS = [
  { id: 'white', name: 'Blank White', url: '' },
  { id: 'universe', name: 'Overture Universe', url: '/images/universe/landscape-v2.png' },
  { id: 'cosmos', name: 'Cosmic Dream', url: '/images/wallpapers/cosmic-dream.png' },
  { id: 'sky', name: 'Pastel Sky', url: '/images/wallpapers/pastel-sky.png' },
  { id: 'forest', name: 'Enchanted Forest', url: '/images/wallpapers/enchanted-forest.png' },
  { id: 'music', name: 'Music Waves', url: '/images/wallpapers/music-waves.png' },
  { id: 'sunset', name: 'Sunset Hills', url: '/images/wallpapers/sunset-hills.png' },
] as const;

type WallpaperScope = 'teacher' | 'squad';

function resizeImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const scale = Math.min(1, 1920 / image.width, 1080 / image.height);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      if (!context) return reject(new Error('Could not process this picture.'));
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.78));
    };
    image.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Please choose a valid image.')); };
    image.src = objectUrl;
  });
}

export default function DashboardWallpaper({ children, scope, scopeId, canEdit = true, defaultWallpaper = 'universe' }: { children: React.ReactNode; scope: WallpaperScope; scopeId: string; canEdit?: boolean; defaultWallpaper?: string }) {
  const [selected, setSelected] = useState(defaultWallpaper);
  const [uploaded, setUploaded] = useState('');
  const [saving, setSaving] = useState(false);
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const preset = WALLPAPERS.find(item => item.id === selected) || WALLPAPERS[0];
  const wallpaper = selected === 'upload' && uploaded ? uploaded : preset.url;

  useEffect(() => {
    setSelected(defaultWallpaper);
    setUploaded('');
    if (!scopeId) return;
    return onSnapshot(doc(db, scope === 'teacher' ? 'users' : 'squads', scopeId), snapshot => {
      const choice = snapshot.data()?.dashboardWallpaper;
      const id = String(choice?.preset || defaultWallpaper);
      const imageUrl = String(choice?.imageUrl || '');
      setUploaded(imageUrl);
      setSelected(id === 'upload' && imageUrl ? id : WALLPAPERS.some(item => item.id === id) ? id : defaultWallpaper);
    }, () => setError('Could not load the saved wallpaper.'));
  }, [scope, scopeId, defaultWallpaper]);

  useEffect(() => {
    const shell = document.querySelector<HTMLElement>('.teacher-shell');
    if (!shell) return;
    shell.style.setProperty('--dashboard-wallpaper-image', wallpaper ? `url("${wallpaper}")` : 'none');
    return () => { shell.style.removeProperty('--dashboard-wallpaper-image'); };
  }, [wallpaper]);

  const saveChoice = async (id: string, imageUrl = '') => {
    if (!scopeId || !canEdit || saving) return;
    setSaving(true);
    setError('');
    try {
      await updateDoc(doc(db, scope === 'teacher' ? 'users' : 'squads', scopeId), {
        dashboardWallpaper: { preset: id, ...((imageUrl || uploaded) ? { imageUrl: imageUrl || uploaded } : {}) },
      });
      setSelected(id);
      if (imageUrl) setUploaded(imageUrl);
    } catch {
      setError('Could not save this wallpaper. Please try again.');
    } finally { setSaving(false); }
  };

  const upload = async (file?: File) => {
    if (!file || !scopeId || !canEdit || saving) return;
    if (!file.type.startsWith('image/')) { setError('Please choose an image file.'); return; }
    setError('');
    setSaving(true);
    try {
      const image = await resizeImage(file);
      const blob = await (await fetch(image)).blob();
      if (blob.size >= 5 * 1024 * 1024) throw new Error('This picture is too large after resizing. Try another image.');
      const path = `${scope === 'teacher' ? 'users' : 'squads'}/${scopeId}/wallpapers/${Date.now()}-${Math.random().toString(36).slice(2)}.jpg`;
      const target = ref(storage, path);
      await uploadBytes(target, blob, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(target);
      await updateDoc(doc(db, scope === 'teacher' ? 'users' : 'squads', scopeId), { dashboardWallpaper: { preset: 'upload', imageUrl: url } });
      setUploaded(url);
      setSelected('upload');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'This picture could not be saved. Please try again.');
    } finally { setSaving(false); }
  };

  return <div
    className="dashboard-wallpaper"
    onClick={event => {
      const target = event.target as HTMLElement;
      if (!canEdit || !scopeId || (target !== event.currentTarget && !['squad-dashboard-page', 'page-content', 'squads-page', 'squads-container', 'games-page', 'games-container', 'teacher-profile-page'].some(name => target.classList.contains(name)))) { setMenu(null); return; }
      const rect = event.currentTarget.getBoundingClientRect();
      setMenu({ x: Math.min(event.clientX - rect.left, rect.width - 190), y: event.clientY - rect.top });
    }}
  >
    {children}
    {menu && <button type="button" className="dashboard-wallpaper-menu" style={{ left: Math.max(8, menu.x), top: menu.y }} onClick={event => { event.stopPropagation(); setMenu(null); setPickerOpen(true); }}>Change wallpaper</button>}
    {pickerOpen && <div className="dashboard-wallpaper-overlay" onClick={event => { event.stopPropagation(); setPickerOpen(false); }}>
      <section className="dashboard-wallpaper-dialog" role="dialog" aria-modal="true" aria-label="Change wallpaper" onClick={event => event.stopPropagation()}>
        <div className="dashboard-wallpaper-heading"><h2>Change wallpaper</h2><button type="button" onClick={() => setPickerOpen(false)} aria-label="Close wallpaper picker">×</button></div>
        <p>Choose a preset or upload your own picture. This choice is saved {scope === 'teacher' ? 'to your teacher profile' : 'for this squad'}.</p>
        <div className="dashboard-wallpaper-grid">
          {WALLPAPERS.map(item => <button type="button" key={item.id} disabled={saving} className={selected === item.id ? 'is-selected' : ''} onClick={() => void saveChoice(item.id)}>{item.url ? <img src={item.url} alt="" /> : <span className="dashboard-wallpaper-white-preview" aria-hidden="true" />}<span>{item.name}</span></button>)}
          {uploaded && <button type="button" disabled={saving} className={selected === 'upload' ? 'is-selected' : ''} onClick={() => void saveChoice('upload', uploaded)}><img src={uploaded} alt="" /><span>Your picture</span></button>}
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="dashboard-wallpaper-file" onChange={event => void upload(event.target.files?.[0])} />
        <button type="button" className="dashboard-wallpaper-upload" disabled={saving} onClick={() => inputRef.current?.click()}>{saving ? 'Saving…' : 'Upload a picture'}</button>
        {error && <p className="dashboard-wallpaper-error" role="alert">{error}</p>}
      </section>
    </div>}
  </div>;
}
